import { useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import type { GridPaginationModel } from "@mui/x-data-grid-premium";
import debounce from "lodash/debounce";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import {
  EMPTY_IMPLEMENTATION_RECORD_FILTER_OPTIONS,
  fetchImplementationRecordFilterOptions,
  fetchImplementationRecords,
  type ImplementationRecord,
  type ImplementationRecordFilterOptions,
} from "../../../services/implementationRecordService";
import { resultLoadErrorMessage } from "../resultLoadError";
import { useAiHubListFiltersStore } from "../aiHubListFiltersStore";
import ImplementationRecordDetailDialog from "./ImplementationRecordDetailDialog";
import ImplementationRecordFilterBar from "./ImplementationRecordFilterBar";
import ImplementationRecordTable from "./ImplementationRecordTable";
import { toAssessmentSourceFilterOptions } from "./implementationRecordView";

const KEYWORD_DEBOUNCE_MS = 300;

export default function ImplementationRecordTab() {
  const { showMessage } = useSnackbar();
  const filters = useAiHubListFiltersStore((state) => state.implementationRecords);
  const setImplementationRecords = useAiHubListFiltersStore(
    (state) => state.setImplementationRecords,
  );

  const [rows, setRows] = useState<ImplementationRecord[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterOptions, setFilterOptions] =
    useState<ImplementationRecordFilterOptions>(
      EMPTY_IMPLEMENTATION_RECORD_FILTER_OPTIONS,
    );
  const [detailRecord, setDetailRecord] = useState<ImplementationRecord | null>(
    null,
  );
  const [keywordInput, setKeywordInput] = useState(filters.keyword);

  const paginationModel = useMemo<GridPaginationModel>(
    () => ({ page: filters.page, pageSize: filters.pageSize }),
    [filters.page, filters.pageSize],
  );

  const sourceOptions = useMemo(
    () => toAssessmentSourceFilterOptions(filterOptions.assessmentSources),
    [filterOptions.assessmentSources],
  );

  useEffect(() => {
    setKeywordInput(filters.keyword);
  }, [filters.keyword]);

  const debouncedKeyword = useMemo(
    () =>
      debounce((value: string) => {
        setImplementationRecords({ keyword: value.trim(), page: 0 });
      }, KEYWORD_DEBOUNCE_MS),
    [setImplementationRecords],
  );

  useEffect(
    () => () => {
      debouncedKeyword.cancel();
    },
    [debouncedKeyword],
  );

  useEffect(() => {
    let mounted = true;
    fetchImplementationRecordFilterOptions()
      .then((options) => {
        if (mounted) setFilterOptions(options);
      })
      .catch((err: unknown) => {
        if (!mounted) return;
        showMessage(
          resultLoadErrorMessage(err, "Failed to load filter options."),
          "error",
        );
      });
    return () => {
      mounted = false;
    };
  }, [showMessage]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    fetchImplementationRecords({
      page: filters.page + 1,
      pageSize: filters.pageSize,
      keyword: filters.keyword || undefined,
      assessmentSource: filters.assessmentSource.length
        ? filters.assessmentSource
        : undefined,
      assessmentName: filters.assessmentName.length
        ? filters.assessmentName
        : undefined,
      impactedObjectType: filters.impactedObjectType.length
        ? filters.impactedObjectType
        : undefined,
    })
      .then((result) => {
        if (!mounted) return;
        setRows(result.items);
        setRowCount(result.total);
      })
      .catch((err: unknown) => {
        if (!mounted) return;
        setRows([]);
        setRowCount(0);
        showMessage(
          resultLoadErrorMessage(err, "Failed to load Executed Assessments."),
          "error",
        );
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [
    filters.assessmentName,
    filters.assessmentSource,
    filters.impactedObjectType,
    filters.keyword,
    filters.page,
    filters.pageSize,
    showMessage,
  ]);

  const handleKeywordChange = useCallback(
    (value: string) => {
      setKeywordInput(value);
      debouncedKeyword(value);
    },
    [debouncedKeyword],
  );

  if (loading && rows.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 320,
          gap: 1,
        }}
      >
        <CircularProgress size={28} />
        <Typography sx={{ fontSize: 13, color: "#6A7282" }}>Loading…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
      <ImplementationRecordFilterBar
        keyword={keywordInput}
        onKeywordChange={handleKeywordChange}
        applied={{
          assessmentSource: filters.assessmentSource,
          assessmentName: filters.assessmentName,
          impactedObjectType: filters.impactedObjectType,
        }}
        onApply={(next) => setImplementationRecords({ ...next, page: 0 })}
        sourceOptions={sourceOptions}
        assessmentNameOptions={filterOptions.assessmentNames}
        impactedObjectTypeOptions={filterOptions.impactedObjectTypes}
      />
      <ImplementationRecordTable
        rows={rows}
        rowCount={rowCount}
        loading={loading}
        paginationModel={paginationModel}
        onPaginationModelChange={(model) =>
          setImplementationRecords({ page: model.page, pageSize: model.pageSize })
        }
        onViewRecord={setDetailRecord}
      />
      <ImplementationRecordDetailDialog
        open={Boolean(detailRecord)}
        record={detailRecord}
        onClose={() => setDetailRecord(null)}
      />
    </Box>
  );
}
